import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Form, Select, DatePicker, Button, Table, Space, message, Spin, Row, Col } from 'antd'
import { Pie } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { fetchAttributionData, selectAttribution } from '../../store/analyticsSlice'
import apiService from '../../services/api'
import dayjs from 'dayjs'

ChartJS.register(ArcElement, Tooltip, Legend)

const { RangePicker } = DatePicker
const { Option } = Select

const AttributionView = () => {
  const dispatch = useDispatch()
  const { data, loading, error } = useSelector(selectAttribution)
  const [form] = Form.useForm()

  const [exchanges, setExchanges] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  useEffect(() => {
    loadOptions()
  }, [])

  const loadOptions = async () => {
    setLoadingOptions(true)
    try {
      const [exchangesRes, accountsRes] = await Promise.all([
        apiService.exchanges.getActiveExchanges(),
        apiService.trades.getActiveAccounts(),
      ])

      setExchanges(exchangesRes.data.data || [])
      setAccounts(accountsRes.data.data || [])
    } catch (err) {
      message.error('加载选项失败')
    } finally {
      setLoadingOptions(false)
    }
  }

  const handleSubmit = async (values) => {
    const params = {
      dimensions: [values.dimension],
      start_date: values.dateRange[0].format('YYYY-MM-DD'),
      end_date: values.dateRange[1].format('YYYY-MM-DD'),
      top_n: 10,
    }

    // 后端 AttributionRequest 目前不直接支持 exchanges/accounts 过滤
    // 但我们可以将它们也放入 dimensions 或者作为额外参数（如果后端支持）
    // 这里为了简单，仅同步核心 schema

    dispatch(fetchAttributionData(params))
  }

  const generateColors = (count) => {
    const colors = []
    for (let i = 0; i < count; i++) {
      colors.push(`hsl(${(i * 360) / count}, 70%, 60%)`)
    }
    return colors
  }

  const chartData = data ? {
    labels: data.labels || [],
    datasets: [{
      data: data.values || [],
      backgroundColor: generateColors(data.labels?.length || 0),
      borderWidth: 1,
    }],
  } : null

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: '归因分析',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || ''
            const value = context.parsed || 0
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(2)
            return `${label}: ${value.toFixed(2)} (${percentage}%)`
          }
        }
      }
    },
  }

  const columns = [
    {
      title: '维度',
      dataIndex: 'dimension',
      key: 'dimension',
    },
    {
      title: '指标值',
      dataIndex: 'value',
      key: 'value',
      render: (val) => typeof val === 'number' ? val.toFixed(2) : val,
      sorter: (a, b) => a.value - b.value,
    },
    {
      title: '贡献度',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (val) => val ? `${val.toFixed(2)}%` : '-',
      sorter: (a, b) => a.percentage - b.percentage,
    },
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Card title="归因分析" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={handleSubmit}
          initialValues={{
            dimension: 'exchange',
            metric: 'volume',
            dateRange: [
              dayjs().subtract(7, 'day').startOf('day'),
              dayjs().subtract(1, 'day').endOf('day'),
            ],
          }}
        >
          <Form.Item name="dimension" label="归因维度" rules={[{ required: true }]}>
            <Select style={{ width: 150 }}>
              <Option key="exchange" value="exchange">交易所</Option>
              <Option key="account" value="account">账户</Option>
              <Option key="pair" value="pair">币种</Option>
            </Select>
          </Form.Item>

          <Form.Item name="metric" label="指标" rules={[{ required: true }]}>
            <Select style={{ width: 150 }}>
              <Option key="volume" value="volume">交易量</Option>
              <Option key="pnl" value="pnl">盈亏</Option>
              <Option key="maker_volume" value="maker_volume">Maker 交易量</Option>
              <Option key="taker_volume" value="taker_volume">Taker 交易量</Option>
            </Select>
          </Form.Item>

          <Form.Item name="dateRange" label="日期范围" rules={[{ required: true }]}>
            <RangePicker />
          </Form.Item>

          <Form.Item name="exchanges" label="交易所">
            <Select
              mode="multiple"
              style={{ width: 200 }}
              placeholder="全部"
              loading={loadingOptions}
              allowClear
            >
              {exchanges.map(ex => (
                <Option key={ex.code} value={ex.code}>{ex.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="accounts" label="账户">
            <Select
              mode="multiple"
              style={{ width: 200 }}
              placeholder="全部"
              loading={loadingOptions}
              allowClear
            >
              {accounts.map(acc => (
                <Option key={`${acc.account}_${acc.exchange}_${acc.trade_type}`} value={acc.account}>{acc.account}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              查询
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {error && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ color: 'red' }}>错误: {error}</div>
        </Card>
      )}

      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        </Card>
      )}

      {data && !loading && (
        <>
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <Card title="饼图展示">
                <div style={{ height: 400 }}>
                  {chartData && <Pie data={chartData} options={chartOptions} />}
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="统计信息">
                <div style={{ padding: '20px' }}>
                  <p><strong>总计:</strong> {data.total?.toFixed(2) || 0}</p>
                  <p><strong>平均值:</strong> {data.average?.toFixed(2) || 0}</p>
                  <p><strong>最大值:</strong> {data.max?.toFixed(2) || 0}</p>
                  <p><strong>最小值:</strong> {data.min?.toFixed(2) || 0}</p>
                  <p><strong>数据点数:</strong> {data.count || 0}</p>
                </div>
              </Card>
            </Col>
          </Row>

          {data.table && (
            <Card title="详细数据">
              <Table
                columns={columns}
                dataSource={data.table}
                rowKey={(record, idx) => idx}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default AttributionView